// Mirror of the form state in the redux store (state.form[formName]).
// redux-form kept the form state in redux and the app reads it with
// selectors in mapStateToProps; the final-form based forms publish their
// state here so that those selectors keep working.

import { getIn } from 'final-form';

import { getRegisteredFormState } from './registry';
import isFormValueEqual from './formEquality';


export const FORM_UPDATE = '@@finalForm/UPDATE';
export const FORM_DESTROY = '@@finalForm/DESTROY';

export const updateFormState = (form, formState) => ({
  type: FORM_UPDATE,
  form,
  formState,
});

export const destroyFormState = form => ({
  type: FORM_DESTROY,
  form,
});

export const formReducer = (state = {}, action) => {
  switch (action.type) {
    case FORM_UPDATE:
      return { ...state, [action.form]: action.formState };
    case FORM_DESTROY: {
      if (!(action.form in state)) {
        return state;
      }
      const { [action.form]: removed, ...rest } = state;
      return rest;
    }
    default:
      return state;
  }
};

const EMPTY = {};

// Before the first publication to redux (during the first render of the form
// children) the state is read from the mounted final-form instance.
const getFormSlice = (state, form) => {
  const slice = state && state.form ? state.form[form] : undefined;
  if (slice) {
    return slice;
  }
  const formState = getRegisteredFormState(form);
  if (!formState) {
    return undefined;
  }
  const values = formState.values || EMPTY;
  const initial = formState.initialValues || EMPTY;
  return {
    values,
    initial,
    dirty: !isFormValueEqual(values, initial),
    submitting: !!formState.submitting,
  };
};

export const getFormValues = form => (state) => {
  const slice = getFormSlice(state, form);
  return slice ? slice.values : undefined;
};

export const getFormInitialValues = form => (state) => {
  const slice = getFormSlice(state, form);
  return slice ? slice.initial : undefined;
};

export const isDirty = form => (state) => {
  const slice = getFormSlice(state, form);
  return !!(slice && slice.dirty);
};

export const isPristine = form => state => !isDirty(form)(state);

export const isSubmitting = form => (state) => {
  const slice = getFormSlice(state, form);
  return !!(slice && slice.submitting);
};

export const formValueSelector = form => (state, ...fieldNames) => {
  const values = getFormValues(form)(state) || {};
  if (fieldNames.length === 1) {
    return getIn(values, fieldNames[0]);
  }
  return fieldNames.reduce((result, fieldName) => ({
    ...result,
    [fieldName]: getIn(values, fieldName),
  }), {});
};
